import { useStore } from '@nanostores/react';
import { accountStore, userStore } from '@/services/auth/store';
import { hasDevHelperAccess } from '@/services/dev-helper/store';
import { postWindowMessage } from './store';

/**
 * The one visibility rule for the experiment simulator: the dock, patched reads and command
 * interception all ask this. It is exactly the developer tools panel's rule, so the simulator runs
 * in development builds (`import.meta.env.DEV` or external UI overrides), on standalone and devbox
 * deployments (the tier decision lives in `services/dev-helper/store.ts`), and in production only
 * for an account with the `developer-tools-panel` flag that may use developer tools. When it is off
 * nothing simulated is read or applied, whatever localStorage holds.
 */
export function isExperimentSimulatorEnabled(): boolean {
  return hasDevHelperAccess(accountStore.get(), userStore.get());
}

/** The same rule as `isExperimentSimulatorEnabled`, re-evaluated when the account or user changes. */
export function useExperimentSimulatorEnabled(): boolean {
  // Subscribed only so the answer is recomputed when either store changes.
  useStore(accountStore);
  useStore(userStore);
  return isExperimentSimulatorEnabled();
}

declare global {
  interface Window {
    /** The simulator's gate as Hydra sees it, for the embedded Ember app (see store.ts). */
    __cioExperimentSimulator?: { gateOpen: boolean };
  }
}

/**
 * Publishes the gate for other readers on this window (Ember): `window.__cioExperimentSimulator`
 * always holds the current answer, and a `gate` message goes out whenever it changes.
 */
export function publishSimulatorGate(open: boolean): void {
  const previous = window.__cioExperimentSimulator?.gateOpen;
  window.__cioExperimentSimulator = { gateOpen: open };
  if (previous !== open) {
    postWindowMessage({ type: 'gate', open });
  }
}
